import { useId } from 'react';
import threadsIcon from '@/assets/icons/threads.webp';
import soundcloudIcon from '@/assets/icons/soundcloud.webp';
import redditIcon from '@/assets/icons/reddit.webp';

interface SocialIconProps {
  slug: string;
  className?: string;
}

export default function SocialIcon({ slug, className = 'h-5 w-5' }: SocialIconProps) {
  const uniqueId = useId();
  const igGradientId = `ig-${uniqueId}`;

  switch (slug) {
    case 'instagram':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none">
          <defs>
            <radialGradient id={igGradientId} cx="30%" cy="107%" r="150%">
              <stop offset="0%" stopColor="#fdf497" />
              <stop offset="5%" stopColor="#fdf497" />
              <stop offset="45%" stopColor="#fd5949" />
              <stop offset="60%" stopColor="#d6249f" />
              <stop offset="90%" stopColor="#285AEB" />
            </radialGradient>
          </defs>
          <rect x="2" y="2" width="20" height="20" rx="5" stroke={`url(#${igGradientId})`} strokeWidth="2" />
          <circle cx="12" cy="12" r="5" stroke={`url(#${igGradientId})`} strokeWidth="2" />
          <circle cx="17.5" cy="6.5" r="1.5" fill={`url(#${igGradientId})`} />
        </svg>
      );
    case 'tiktok':
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.41a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.84z" fill="currentColor" />
        </svg>
      );
    case 'youtube':
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <path d="M23.5 6.19a3.02 3.02 0 00-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 00.5 6.19 31.6 31.6 0 000 12a31.6 31.6 0 00.5 5.81 3.02 3.02 0 002.12 2.14c1.88.55 9.38.55 9.38.55s7.5 0 9.38-.55a3.02 3.02 0 002.12-2.14A31.6 31.6 0 0024 12a31.6 31.6 0 00-.5-5.81z" fill="#FF0000" />
          <path d="M9.55 15.57l6.27-3.57-6.27-3.57v7.14z" fill="#fff" />
        </svg>
      );
    case 'twitter':
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor" />
        </svg>
      );
    case 'spotify':
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <circle cx="12" cy="12" r="12" fill="#1DB954" />
          <path d="M16.94 16.47a.75.75 0 01-1.03.25c-2.82-1.72-6.37-2.11-10.55-1.16a.75.75 0 11-.34-1.46c4.57-1.05 8.49-.6 11.67 1.34a.75.75 0 01.25 1.03zm1.26-2.83a.94.94 0 01-1.29.31c-3.23-1.98-8.15-2.56-11.97-1.4a.94.94 0 11-.54-1.8c4.36-1.32 9.78-.68 13.49 1.6a.94.94 0 01.31 1.29zm.11-2.94C14.96 8.67 8.69 8.45 5.06 9.54a1.13 1.13 0 11-.65-2.16c4.17-1.25 11.1-.99 15.48 1.62a1.13 1.13 0 01-.58 2.1v-.4z" fill="#fff" />
        </svg>
      );
    case 'twitch':
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <path d="M2.149 0L.537 4.119v16.836h5.731V24h3.224l3.045-3.045h4.657l6.269-6.269V0H2.149zm1.612 1.612h17.611v11.496l-3.582 3.582h-5.731L9.015 19.74v-3.045H3.761V1.612zM10.09 5.373v6.27h1.613v-6.27H10.09zm4.657 0v6.27h1.612v-6.27h-1.612z" fill="#9146FF" />
        </svg>
      );
    case 'facebook':
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2" />
        </svg>
      );
    case 'telegram':
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.94z" fill="#0088CC" />
        </svg>
      );
    case 'linkedin':
      return (
        <svg viewBox="0 0 24 24" className={className}>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2" />
        </svg>
      );
    case 'threads':
      return <img src={threadsIcon} alt="Threads" className={className + ' rounded-sm object-contain'} />;
    case 'soundcloud':
      return <img src={soundcloudIcon} alt="SoundCloud" className={className + ' rounded-full object-contain'} />;
    case 'reddit':
      return <img src={redditIcon} alt="Reddit" className={className + ' rounded-full object-contain'} />;
    default:
      return <span className="text-base">📦</span>;
  }
}
